<section
  class="child-work"
  data-child-work-owner
  [attr.inert]="pickerOpen ? '' : null"
  [attr.aria-label]="
    terminal ? 'Workflow result settings' : 'Related work settings'
  "
>
  <ng-container *ngIf="editorView as editor">
  <header>
    <div>
      <h4 tabindex="-1">{{ terminal ? "Workflow result" : "Related work" }}</h4>
      <p>
        {{
          terminal
            ? "Declare the result recorded when this completion point is reached."
            : "Keep related work with this exact visit and choose which recorded results it requires."
        }}
      </p>
    </div>
    <button
      uiButton
      type="button"
      (click)="loadCatalog()"
      [disabled]="disabled || loading"
    >
      {{ terminal ? "Refresh availability" : "Refresh available workflows" }}
    </button>
  </header>
  <p *ngIf="loading" role="status">
    {{
      terminal
        ? "Checking result authoring availability…"
        : "Loading published child workflows…"
    }}
  </p>
  <p *ngIf="childWorkUnavailableReason" role="status">
    {{ childWorkUnavailableReason }}
  </p>
  <p *ngIf="loadError" role="alert">{{ loadError }}</p>
  <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p>
  <ng-container *ngIf="terminal; else links">
    <p *ngIf="!node?.terminalOutcome">
      No workflow result is configured for this completion point.
    </p>
    <section *ngIf="node?.terminalOutcome as outcome" class="result-summary" data-workflow-result-summary aria-label="Configured workflow result">
      <ng-container *ngIf="outcomeEditable; else unavailableResultSummary">
        <h5>{{ outcome.label || 'Unnamed saved result' }}</h5>
        <p class="result-code"><span>Saved result code</span><code data-saved-workflow-result-code>{{ outcome.code }}</code></p>
      </ng-container>
      <ng-template #unavailableResultSummary><p>The saved workflow result is unavailable for editing. Its original values are preserved.</p></ng-template>
    </section>
    <details *ngIf="!outcomeEditable">
      <summary>Unavailable saved result</summary>
      <pre>{{ node?.terminalOutcome | json }}</pre>
    </details>
    <div class="actions result-actions">
      <button
        uiButton
        type="button"
        data-edit-workflow-result
        (click)="beginOutcome(editor.origin)"
        [disabled]="
          disabled ||
          interactionBlocked ||
          (!node?.terminalOutcome && editingDisabled) ||
          !outcomeEditable
        "
      >
        {{
          node?.terminalOutcome
            ? !editingDisabled
              ? "Edit result"
              : "Inspect result"
            : "Add workflow result"
        }}</button
      ><button
        *ngIf="node?.terminalOutcome"
        data-remove-workflow-result
        uiButton
        type="button"
        (click)="removeOutcome(editor.origin)"
        [disabled]="editingDisabled"
      >
        Remove result
      </button>
    </div>
    <section
      *ngIf="outcomeDraft as outcome"
      class="child-form"
      aria-label="Completion result"
    >
      <label
        >Result name<input
          uiControl
          data-result-name
          maxlength="120"
          [ngModel]="outcome.label"
          (ngModelChange)="outcomeLabelChanged($event, editor.origin, outcomeName)"
          #outcomeName="ngModel"
          [disabled]="editingDisabled"
      /></label>
      <p class="policy-hint">Up to 120 characters.</p>
      <div class="result-code"><strong>{{ editingExistingOutcome ? 'Saved result code' : 'Candidate result code' }}</strong>
        <code data-workflow-result-code>{{ outcome.code || 'Enter a result name to derive its code.' }}</code>
        <p class="policy-hint">{{ editingExistingOutcome ? 'Changing the name retains this saved code.' : 'This code is derived from the name until the new result is applied.' }}</p>
      </div>
      <p class="hint">{{ editingExistingOutcome ? 'Save draft also applies valid edits to this saved result.' : 'Apply this new result explicitly before saving the draft.' }}</p>
      <div class="actions">
        <button
          uiButton
          type="button"
          (click)="apply(editor.origin)"
          [disabled]="editingDisabled"
        >
          Apply result</button
        ><button uiButton type="button" data-cancel-result-edits (click)="discardVisibleChanges(editor.origin)">
          Cancel result edits
        </button>
      </div>
    </section>
    <p class="hint">
      A result is recorded by actual workflow completion. It does not finalize a
      connected application record or complete a parent workflow.
    </p>
  </ng-container>
  <ng-template #links>
    <p *ngIf="opaqueRules" role="status">Saved related-work settings are unavailable for editing and remain preserved.</p>
    <pre *ngIf="opaqueRules">{{ node?.declaration?.behavior?.childLinks | json }}</pre>
    <p *ngIf="!rules.length && !opaqueRules">No related work is configured for this step.</p>
    <p *ngIf="ambiguous" role="alert">
      Saved related-work identities are ambiguous. Their settings are preserved
      for inspection.
    </p>
    <ng-container *ngFor="let rule of rules">
      <article *ngIf="ruleSummary(rule) as summary" class="configured-rule" data-related-work-summary>
        <header class="configured-rule__header">
          <div class="configured-rule__identity"><h5>{{ summary.name }}</h5><p>{{ summary.publication }}</p></div>
          <div class="actions">
            <button uiButton type="button" data-edit-related-work [attr.aria-label]="(editingDisabled || summary.availability ? 'Inspect related work: ' : 'Edit related work: ') + summary.name"
              (click)="select(summary.rule, editor.origin)" [disabled]="disabled || interactionBlocked || loading || ambiguous || !canInspectEdit(summary.rule)">
              {{ editingDisabled || summary.availability ? 'Inspect' : 'Edit' }}
            </button>
            <button uiButton type="button" data-remove-related-work [attr.aria-label]="'Remove related work: ' + summary.name"
              (click)="remove(summary.rule, editor.origin)" [disabled]="editingDisabled || ambiguous">Remove</button>
          </div>
        </header>
        <dl class="configured-rule__summary">
          <div><dt>Allowed results</dt><dd>{{ summary.results }}</dd></div>
          <div><dt>Mode</dt><dd>{{ summary.mode }}</dd></div>
        </dl>
        <p *ngIf="summary.availability" class="hint" role="status">{{ summary.availability }}</p>
        <details class="relationship-policies" data-related-work-policies>
          <summary>Relationship policies</summary>
          <dl class="policy-summary">
            <div><dt>On return</dt><dd>{{ summary.returnPolicy }}</dd></div>
            <div><dt>When the parent visit ends</dt><dd>{{ summary.cancelPolicy }}</dd></div>
            <div><dt>If child work fails</dt><dd>{{ summary.failurePolicy }}</dd></div>
            <div><dt>Depth limit</dt><dd>{{ summary.depth }}</dd></div>
          </dl>
        </details>
        <details *ngIf="!canInspectEdit(summary.rule) || ambiguous || !summary.fixedPolicySupported || summary.availability" data-related-work-raw>
          <summary>Saved settings for inspection</summary><pre>{{ summary.rule | json }}</pre>
        </details>
      </article>
    </ng-container>
    <button
      uiButton
      type="button"
      (click)="beginAdd(editor.origin)"
      [disabled]="editingDisabled || ambiguous || rules.length >= 16"
    >
      Add related work
    </button>
    <p
      *ngIf="
        catalogReady && !childWorkUnavailableReason && !availableChoices.length
      "
      role="status"
    >
      {{
        choices.length
          ? "No published child version is currently available for selection. The child selector shows why each listed version is unavailable."
          : "No compatible published workflow with declared terminal results is available. Configure and publish a child workflow result first."
      }}
    </p>
    <section
      *ngIf="draft as rule"
      class="child-form"
      aria-label="Related-work rule"
    >
      <div class="settings">
        <label
          >Related-work name<input
            uiControl
            maxlength="160"
            [ngModel]="rule.label"
            #labelModel="ngModel"
            (ngModelChange)="ruleValueChanged('label', $event, editor.origin, labelModel)"
            [disabled]="ruleEditingDisabled"
        /></label>
        <label *ngIf="!workspaceMode"
          >Child workflow version<select
            uiControl
            [ngModel]="childSelection"
            (ngModelChange)="chooseChild($event)"
            [disabled]="ruleEditingDisabled"
          >
            <option value="">Choose a published workflow version</option>
            <option
              *ngIf="unavailableChild && !selectedChild"
              [value]="childSelection"
            >
              Saved child version unavailable · retained
            </option>
            <option
              *ngFor="let child of choices"
              [value]="referenceIdentity(child)"
              [disabled]="!child.availableForNewUse"
            >
              {{ child.displayName }} · {{ child.definitionVersionCode
              }}{{
                !child.availableForNewUse
                  ? " · " +
                    (child.unavailableReason || "Unavailable for new use")
                  : ""
              }}
            </option>
          </select></label
        >
        <div *ngIf="workspaceMode" data-child-publication-summary><strong>Child publication</strong><p>{{ rule.child?.workflowCode }} · {{ rule.child?.definitionVersionCode }}</p><button uiButton type="button" data-change-child-publication [disabled]="editingDisabled" (click)="openPublicationPicker(editor.origin)">Change publication</button></div>
        <label
          >Mode<select
            uiControl
            [ngModel]="rule.mode"
            #modeModel="ngModel"
            (ngModelChange)="ruleValueChanged('mode', $event, editor.origin, modeModel)"
            [disabled]="ruleEditingDisabled"
          >
            <option value="">Choose mode</option>
            <option value="blocking">Wait for result</option>
            <option value="observational">Track only</option>
          </select><span class="policy-hint">{{ rule.mode === 'blocking' ? 'Completion waits for an explicitly accepted child result.' : rule.mode === 'observational' ? 'Track child work without blocking this parent step.' : 'Choose whether an accepted result is required.' }}</span></label>
        <ng-container *ngIf="!editingExistingRule">
        <label
          >On return<select
            uiControl
            [ngModel]="rule.returnCode"
            #returnCodeModel="ngModel"
            (ngModelChange)="ruleValueChanged('returnCode', $event, editor.origin, returnCodeModel)"
            [disabled]="ruleEditingDisabled"
          >
            <option value="">Choose return rule</option>
            <option value="retain_original_visit">
              Keep original visit
            </option>
          </select><span class="policy-hint">The relationship stays with the visit that created it.</span></label>
        <label
          >When parent visit ends<select
            uiControl
            [ngModel]="rule.cancelCode"
            #cancelCodeModel="ngModel"
            (ngModelChange)="ruleValueChanged('cancelCode', $event, editor.origin, cancelCodeModel)"
            [disabled]="ruleEditingDisabled"
          >
            <option value="">Choose cancellation rule</option>
            <option value="stop_pending_leave_running">
              Stop pending only
            </option>
          </select><span class="policy-hint">Stop pending creation; leave a running child independent.</span></label>
        <label
          >If child work fails<select
            uiControl
            [ngModel]="rule.failureCode"
            #failureCodeModel="ngModel"
            (ngModelChange)="ruleValueChanged('failureCode', $event, editor.origin, failureCodeModel)"
            [disabled]="ruleEditingDisabled"
          >
            <option value="">Choose failure rule</option>
            <option value="blocked">Leave unaccepted</option>
          </select><span class="policy-hint">Failure leaves the result unaccepted and does not complete this parent step.</span></label>
        </ng-container>
        <label
          >Depth limit<input
            uiControl
            type="number"
            min="1"
            max="16"
            step="1"
            [ngModel]="rule.maximumDepth"
            #maximumDepthModel="ngModel"
            (ngModelChange)="ruleValueChanged('maximumDepth', $event, editor.origin, maximumDepthModel)"
            [disabled]="ruleEditingDisabled"
        /><span class="policy-hint">Choose a maximum depth from 1 to 16.</span></label>
      </div>
      <details *ngIf="editingExistingRule" class="relationship-policies" data-configured-rule-policies>
        <summary>Fixed relationship policies</summary>
        <dl class="policy-summary">
          <div><dt>On return</dt><dd>{{ rule.returnCode === 'retain_original_visit' ? 'Keep the relationship with its original visit.' : 'The saved return policy is unavailable.' }}</dd></div>
          <div><dt>When the parent visit ends</dt><dd>{{ rule.cancelCode === 'stop_pending_leave_running' ? 'Stop pending creation; leave a running child independent.' : 'The saved cancellation policy is unavailable.' }}</dd></div>
          <div><dt>If child work fails</dt><dd>{{ rule.failureCode === 'blocked' ? 'Leave the result unaccepted.' : 'The saved failure policy is unavailable.' }}</dd></div>
        </dl>
        <pre *ngIf="!draftFixedPolicySupported">{{ rule | json }}</pre>
      </details>
      <dl class="settings start-policy" aria-label="Child start policy">
        <div>
          <dt>Child record</dt>
          <dd>
            {{
              selectedChild?.availableForNewUse
                ? "Independent process with no linked record."
                : "Only independent processes with no linked record can start here."
            }}
            No parent record or starting values are copied.
          </dd>
        </div>
        <div>
          <dt>Version and results</dt>
          <dd>
            The selected published version and accepted results stay with this
            relationship. Retries use the same version and results.
          </dd>
        </div>
      </dl>
      <fieldset
        [disabled]="ruleEditingDisabled || !selectedChild?.availableForNewUse"
      >
        <legend>Accepted child results</legend>
        <label *ngFor="let outcome of selectedOutcomes"
          ><input
            uiControl
            type="checkbox"
            [ngModel]="rule.acceptedOutcomeCodes.includes(outcome.code)" #acceptedModel="ngModel"
            (ngModelChange)="toggleOutcome(outcome.code, $event, editor.origin, acceptedModel)"
          />{{ outcome.label }}</label
        >
        <p *ngIf="!selectedChild">
          Select a child workflow version to choose its recorded results.
        </p>
      </fieldset>
      <p *ngIf="unavailableChild" role="status">
        {{
          selectedChild?.unavailableReason ||
            "The exact saved child version is unavailable."
        }}
        It is preserved until you explicitly choose a replacement.
      </p>
      <div class="actions">
        <button
          uiButton
          type="button"
          (click)="apply(editor.origin)"
          [disabled]="editingDisabled"
        >
          Apply related work</button
        ><button uiButton type="button" (click)="discardVisibleChanges(editor.origin)">
          Cancel rule edits
        </button>
      </div>
    </section>
    <p class="hint">
      Entry records pending related work and any required result before the
      configured worker starts it. Publication checks depth and cycles; pending
      related work has not started.
    </p>
  </ng-template>
  </ng-container>
</section>

<ng-template #publicationDetails>
  <section *ngIf="pickerSelection as selected" class="child-publication-details" data-child-publication-details aria-label="Selected child publication">
    <h4>{{ selected.displayName || selected.workflowCode }}</h4>
    <p>{{ selected.workflowCode }} · {{ selected.definitionVersionCode }}</p>
    <p>This exact published version is retained. No parent record, answers or starting values are copied.</p>
    <strong>Published results</strong><ul><li *ngFor="let outcome of selected.terminalOutcomes">{{ outcome.label }} · {{ outcome.code }}</li></ul>
    <button uiButton type="button" data-configure-child-publication (click)="configurePublication(pickerContext)">Select &amp; configure</button>
  </section>
</ng-template>
<phms-workflow-library-selector *ngIf="workspaceMode" title="Choose a child publication" description="Review an exact published workflow and its results before configuring the relationship."
  searchLabel="Search published workflows" allCategoryLabel="All publications" [entries]="pickerEntries" [open]="pickerOpen" [active]="pickerActive"
  [context]="pickerContext" [loading]="loading" [error]="loadError" [statusTemplate]="publicationDetails"
  (chooseRequested)="reviewPublication($event)" (closed)="publicationPickerClosed()" (retryRequested)="loadCatalog()" />
